import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'

import { Button } from '@/components/primitives/Button'
import { stopImpersonation, useImpersonation } from '@/lib/auth/session'

/**
 * High-visibility amber banner rendered across all pages during active impersonation.
 *
 * Acceptance Criterion 10:
 * "Impersonation session renders a high-visibility amber banner across all pages with
 * 'Return to Admin' button, times out in 30 minutes, enforces read-only mode,
 * and logs all actions to platform audit logs."
 */
export function ImpersonationBanner() {
  const session = useImpersonation()
  const navigate = useNavigate()
  const [secondsRemaining, setSecondsRemaining] = useState<number>(1800)

  useEffect(() => {
    if (!session?.expiresAt) return

    function update() {
      if (!session) return
      const diff = Math.max(
        0,
        Math.floor((new Date(session.expiresAt).getTime() - Date.now()) / 1000),
      )
      setSecondsRemaining(diff)
      if (diff <= 0) {
        stopImpersonation()
        void navigate('/admin')
      }
    }

    update()
    const timer = setInterval(update, 1000)
    return () => clearInterval(timer)
  }, [session, navigate])

  if (!session) return null

  const minutes = Math.floor(secondsRemaining / 60)
  const seconds = secondsRemaining % 60
  const timeFormatted = `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`

  function handleReturn() {
    stopImpersonation()
    void navigate('/admin')
  }

  return (
    <div
      role="alert"
      className="flex items-center justify-between border-b border-warning/30 bg-warning-subtle px-4 py-2 text-caption text-warning"
    >
      <div className="flex items-center gap-2 font-medium">
        <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
        <span>
          <strong>Impersonation Session (Read-Only Mode)</strong> — Acting on workspace{' '}
          <code className="rounded bg-warning/10 px-1 py-0.5">{session.workspaceSlug}</code>
        </span>
      </div>

      <div className="flex items-center gap-4">
        <span className="font-mono text-xs">
          Expires in: <strong className="font-semibold">{timeFormatted}</strong>
        </span>
        <Button
          size="sm"
          variant="secondary"
          onClick={handleReturn}
          className="border-warning text-warning hover:bg-warning/10"
        >
          Return to Admin
        </Button>
      </div>
    </div>
  )
}
